import { useState } from 'react';
import { useQuery, useMutation } from 'convex/react';
import { api } from '../../convex/_generated/api';
import type { Id } from '../../convex/_generated/dataModel';

type AttendanceStatus = "present" | "absent" | "late" | "excused";

// ════════════════════════════════════════════════════════════
// QUERY HOOKS
// ════════════════════════════════════════════════════════════

export function useSessionAttendance(sessionId: string) {
  const data = useQuery(
    api.coach.attendance.getForSession,
    sessionId ? { sessionId: sessionId as Id<"trainingSessions"> } : 'skip'
  );
  return { data, isLoading: data === undefined, error: null };
}

/** Players assigned to this coach not yet added to the session roster. */
export function useAssignedPlayers(sessionId: string) {
  const data = useQuery(
    api.coach.attendance.getAssignedPlayers,
    sessionId ? { sessionId: sessionId as Id<"trainingSessions"> } : 'skip'
  );
  return { data: data ?? [], isLoading: data === undefined };
}

// ════════════════════════════════════════════════════════════
// MUTATION HOOKS
// ════════════════════════════════════════════════════════════

export function useAddPlayersToSession(sessionId: string) {
  const mutate = useMutation(api.coach.attendance.addPlayersToSession);
  return {
    mutate: (playerIds: string[]) =>
      mutate({
        sessionId: sessionId as Id<"trainingSessions">,
        playerIds: playerIds as Id<"players">[],
      }),
  };
}

export function useMarkAttendance(sessionId: string) {
  const mutate = useMutation(api.coach.attendance.mark);
  const [isPending, setIsPending] = useState(false);
  return {
    isPending,
    mutateAsync: async (args: { playerId: string; status: string; makeupRequired?: boolean }) => {
      setIsPending(true);
      try {
        return await mutate({
          sessionId: sessionId as Id<"trainingSessions">,
          playerId: args.playerId as Id<"players">,
          status: args.status as AttendanceStatus,
          makeupRequired: args.makeupRequired,
        });
      } finally {
        setIsPending(false);
      }
    },
    mutate: (args: { playerId: string; status: string; makeupRequired?: boolean }) => {
      mutate({
        sessionId: sessionId as Id<"trainingSessions">,
        playerId: args.playerId as Id<"players">,
        status: args.status as AttendanceStatus,
        makeupRequired: args.makeupRequired,
      });
    },
  };
}

export function useCancelMakeup(sessionId: string) {
  const mutate = useMutation(api.coach.attendance.cancelMakeup);
  const [isPending, setIsPending] = useState(false);
  return {
    isPending,
    mutateAsync: async (args: { playerId: string }) => {
      setIsPending(true);
      try {
        return await mutate({
          sessionId: sessionId as Id<"trainingSessions">,
          playerId: args.playerId as Id<"players">,
        });
      } finally {
        setIsPending(false);
      }
    },
    mutate: (args: { playerId: string }) => {
      mutate({
        sessionId: sessionId as Id<"trainingSessions">,
        playerId: args.playerId as Id<"players">,
      });
    },
  };
}

export function useMarkAllPresent(sessionId: string) {
  const markAttendance = useMutation(api.coach.attendance.mark);
  const [isPending, setIsPending] = useState(false);
  return {
    isPending,
    mutateAsync: async (playerIds: string[]) => {
      setIsPending(true);
      try {
        await Promise.all(
          playerIds.map((playerId) =>
            markAttendance({ sessionId: sessionId as Id<"trainingSessions">, playerId: playerId as Id<"players">, status: 'present' })
          )
        );
      } finally {
        setIsPending(false);
      }
    },
    mutate: (playerIds: string[]) => {
      playerIds.forEach((playerId) => {
        markAttendance({ sessionId: sessionId as Id<"trainingSessions">, playerId: playerId as Id<"players">, status: 'present' });
      });
    },
  };
}
